import useSWR, { type BareFetcher, type SWRConfiguration } from 'swr'

import { type OldreportHealthReport } from '@clinic/client'
import {
  getHealthReportList,
  createHealthReport,
  deleteHealthReport
} from '~/api'
import { useMutation } from '~/hooks/use-mutation'

export type HealthReportListItem = OldreportHealthReport

const REPORT_LIST_CACHE_KEY = 'cluster-health-report-list'

/**
 * Hook for getting health report list
 */
export function useGetReportList(
  {
    orgId,
    clusterId,
    startTime,
    endTime,
    brief = true
  }: {
    orgId: string
    clusterId: string
    startTime: number
    endTime: number
    brief?: boolean
  },
  config?: SWRConfiguration<
    HealthReportListItem[] | undefined,
    any,
    BareFetcher<HealthReportListItem[] | undefined>
  >
) {
  const { data, error, mutate } = useSWR(
    {
      cacheId: REPORT_LIST_CACHE_KEY,
      orgId,
      clusterId,
      startTime,
      endTime,
      brief
    },
    (args) =>
      getHealthReportList({
        orgId: args.orgId,
        clusterId: args.clusterId,
        startTime: args.startTime,
        endTime: args.endTime,
        brief: args.brief
      }),
    config
  )

  return {
    data: data || [],
    isLoading: !error && !data,
    isError: !!error,
    mutate
  }
}

/**
 * Hook for creating a health report
 */
export function useCreateReport() {
  const { run: mutate, ...mutation } = useMutation(createHealthReport)

  return {
    ...mutation,
    mutate
  }
}

export function useDeleteReport() {
  const { run: mutate, ...mutation } = useMutation(deleteHealthReport)

  return {
    ...mutation,
    mutate
  }
}
